import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users, Package, Image as ImageIcon, Edit, Trash2, Plus, Save, X, Upload, Crown, BarChart3, AlertTriangle, Settings
} from "lucide-react";
import { toast, Toaster } from "sonner";
import PackageManagement from "@/components/admin/PackageManagement";
import VisitorAnalytics from "@/components/admin/VisitorAnalytics";
import MaintenanceMode from "@/components/admin/MaintenanceMode";
import SystemSettings from "@/components/admin/SystemSettings";
import { useLocation } from "react-router-dom";
import { API, BACKEND_URL, getAuthHeaders } from "@/lib/api";
const API_LOCAL_FALLBACK = `${BACKEND_URL}/api`;

export default function AdminDashboardNew() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    name: "",
    role: "user",
    package_id: "basic_free"
  });

  // Logo & Favicon state
  const [currentLogo, setCurrentLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [currentFavicon, setCurrentFavicon] = useState(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  // Branding colors state
  const [brandingColors, setBrandingColors] = useState({
    primary_color: "#2a6a68",
    accent_color: "#10b981",
    text_color: "#1f2937",
    background_color: "#ffffff"
  });
  const [savingColors, setSavingColors] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Update active tab based on URL path
  useEffect(() => {
    if (location.pathname.includes("/admin/settings")) {
      setActiveTab("settings");
    } else if (location.pathname.includes("/admin/branding")) {
      setActiveTab("branding");
    } else if (location.pathname.includes("/admin/dashboard")) {
      setActiveTab("users"); // Default or overview
    }
  }, [location.pathname]);

  const fetchData = async () => {
    try {
      const authHeaders = { headers: getAuthHeaders() };
      const [usersRes, packagesRes, logoRes, colorsRes, faviconRes] = await Promise.all([
        axios.get(`${API}/admin/users`, authHeaders),
        axios.get(`${API}/packages`, authHeaders),
        axios.get(`${API}/branding/logo`),
        axios.get(`${API}/branding/colors`),
        axios.get(`${API}/branding/favicon`)
      ]);

      // Handle both array and object response formats
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || []);
      setUsers(usersData);
      setPackages(packagesRes.data);
      setCurrentLogo(logoRes.data.logo_url);
      setCurrentFavicon(faviconRes.data.favicon_url);

      // Set branding colors
      if (colorsRes.data) {
        setBrandingColors(colorsRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveColors = async () => {
    setSavingColors(true);
    try {
      await axios.post(`${API}/admin/branding/colors`, brandingColors, { headers: getAuthHeaders() });
      toast.success("Farben erfolgreich gespeichert! Bitte laden Sie die Seite neu, um die Änderungen überall zu sehen.");
      await fetchData();
    } catch (error) {
      console.error("Failed to save colors:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern der Farben");
    } finally {
      setSavingColors(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.name) {
      toast.error("Bitte E-Mail und Name eingeben");
      return;
    }

    try {
      await axios.post(`${API}/admin/users`, newUser, { headers: getAuthHeaders() });
      toast.success("Benutzer erfolgreich erstellt!");
      setShowCreateUser(false);
      setNewUser({ email: "", name: "", role: "user", package_id: "basic_free" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates, { headers: getAuthHeaders() });
      toast.success("Benutzer aktualisiert!");
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Aktualisieren");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Möchten Sie diesen Benutzer wirklich löschen? Alle seine Events werden ebenfalls gelöscht.")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      toast.success("Benutzer gelöscht");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleUploadLogo = async (file) => {
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo ist zu groß (max. 2MB)");
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/branding/logo`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data"
        }
      });
      setCurrentLogo(response.data.logo_url);
      toast.success("Logo erfolgreich hochgeladen!");
      // Just refresh the data instead of reloading the entire page
      await fetchData();
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!confirm("Möchten Sie das Logo wirklich löschen?")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/branding/logo`, {
        headers: getAuthHeaders()
      });
      setCurrentLogo(null);
      toast.success("Logo erfolgreich gelöscht");
      await fetchData();
    } catch (error) {
      console.error("Logo delete error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleUploadFavicon = async (file) => {
    if (!file) return;

    if (file.size > 500 * 1024) {
      toast.error("Favicon ist zu groß (max. 500KB)");
      return;
    }

    setUploadingFavicon(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/branding/favicon`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data"
        }
      });
      setCurrentFavicon(response.data.favicon_url);
      toast.success("Favicon erfolgreich hochgeladen!");
      await fetchData();
    } catch (error) {
      console.error("Favicon upload error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleDeleteFavicon = async () => {
    if (!confirm("Möchten Sie das Favicon wirklich löschen?")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/branding/favicon`, {
        headers: getAuthHeaders()
      });
      setCurrentFavicon(null);
      toast.success("Favicon erfolgreich gelöscht");
      await fetchData();
    } catch (error) {
      console.error("Favicon delete error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Administrator",
      user: "Benutzer",
      moderator: "Moderator"
    };
    return labels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: "bg-red-500/10 text-red-500 border-red-500/20",
      moderator: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      user: "bg-green-500/10 text-green-500 border-green-500/20"
    };
    return colors[role] || colors.user;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-40">
      <Toaster position="top-center" richColors />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
                <Crown className="w-8 h-8 text-primary" />
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground mt-1">
                Benutzerverwaltung, Pakete und Branding
              </p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="users" className="gap-2">
                <Users className="w-4 h-4" />
                Benutzer
              </TabsTrigger>
              <TabsTrigger value="packages" className="gap-2">
                <Package className="w-4 h-4" />
                Pakete
              </TabsTrigger>
              <TabsTrigger value="analytics" className="gap-2">
                <BarChart3 className="w-4 h-4" />
                Besucher
              </TabsTrigger>
              <TabsTrigger value="maintenance" className="gap-2">
                <AlertTriangle className="w-4 h-4" />
                Wartung
              </TabsTrigger>
              <TabsTrigger value="branding" className="gap-2">
                <ImageIcon className="w-4 h-4" />
                Branding
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-2">
                <Settings className="w-4 h-4" />
                System
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Alle Benutzer ({users.length})</h2>
                <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                  <DialogTrigger asChild>
                    <Button className="gap-2">
                      <Plus className="w-4 h-4" />
                      Neuer Benutzer
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Neuen Benutzer erstellen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>E-Mail *</Label>
                        <Input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          placeholder="user@example.com"
                        />
                      </div>
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={newUser.name}
                          onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                          placeholder="Max Mustermann"
                        />
                      </div>
                      <div>
                        <Label>Rolle</Label>
                        <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">Benutzer</SelectItem>
                            <SelectItem value="moderator">Moderator</SelectItem>
                            <SelectItem value="admin">Administrator</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Paket</Label>
                        <Select value={newUser.package_id} onValueChange={(val) => setNewUser({ ...newUser, package_id: val })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {packages.map((pkg) => (
                              <SelectItem key={pkg.id} value={pkg.id}>
                                {pkg.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleCreateUser} className="w-full">
                        Benutzer erstellen
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary/30">
                      <tr>
                        <th className="text-left p-4 font-semibold">Name</th>
                        <th className="text-left p-4 font-semibold">E-Mail</th>
                        <th className="text-left p-4 font-semibold">Rolle</th>
                        <th className="text-left p-4 font-semibold">Paket</th>
                        <th className="text-left p-4 font-semibold">Paketstatus</th>
                        <th className="text-right p-4 font-semibold">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-t border-border/50 hover:bg-secondary/20">
                          {editingUser?.id === user.id ? (
                            <>
                              <td className="p-4">
                                <Input
                                  value={editingUser.name}
                                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                                  className="max-w-xs"
                                />
                              </td>
                              <td className="p-4">
                                <Input
                                  type="email"
                                  value={editingUser.email}
                                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                  className="max-w-xs"
                                />
                              </td>
                              <td className="p-4">
                                <Select
                                  value={editingUser.role}
                                  onValueChange={(val) => setEditingUser({ ...editingUser, role: val })}
                                >
                                  <SelectTrigger className="max-w-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="user">Benutzer</SelectItem>
                                    <SelectItem value="moderator">Moderator</SelectItem>
                                    <SelectItem value="admin">Administrator</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-4">
                                <Select
                                  value={editingUser.package_id}
                                  onValueChange={(val) => setEditingUser({ ...editingUser, package_id: val })}
                                >
                                  <SelectTrigger className="max-w-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {packages.map((pkg) => (
                                      <SelectItem key={pkg.id} value={pkg.id}>
                                        {pkg.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-4">
                                <Select
                                  value={editingUser.package_status || "none"}
                                  onValueChange={(val) => setEditingUser({ ...editingUser, package_status: val })}
                                >
                                  <SelectTrigger className="max-w-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">Kein Paket</SelectItem>
                                    <SelectItem value="active">Aktiv</SelectItem>
                                    <SelectItem value="expired">Abgelaufen</SelectItem>
                                    <SelectItem value="cancelled">Gekündigt</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-4 text-right space-x-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleUpdateUser(user.id, {
                                    name: editingUser.name,
                                    email: editingUser.email,
                                    role: editingUser.role,
                                    package_id: editingUser.package_id,
                                    package_status: editingUser.package_status
                                  })}
                                  className="gap-1"
                                >
                                  <Save className="w-3 h-3" />
                                  Speichern
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingUser(null)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="p-4 font-medium">{user.name}</td>
                              <td className="p-4 text-muted-foreground">{user.email}</td>
                              <td className="p-4">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                                  {getRoleLabel(user.role)}
                                </span>
                              </td>
                              <td className="p-4">
                                <span className="text-sm">
                                  {packages.find(p => p.id === user.package_id)?.name || user.package_id || "Keins"}
                                </span>
                              </td>
                              <td className="p-4">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${user.package_status === 'active' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-gray-500/10 text-gray-500 border-gray-500/20'}`}>
                                  {user.package_status === 'active' ? 'Aktiv' : user.package_status === 'none' ? 'Keins' : user.package_status || 'Unbekannt'}
                                </span>
                              </td>
                              <td className="p-4 text-right space-x-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingUser({ ...user })}
                                  className="gap-1"
                                >
                                  <Edit className="w-3 h-3" />
                                  Bearbeiten
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="gap-1"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Löschen
                                </Button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Packages Tab */}
            <TabsContent value="packages">
              <PackageManagement />
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <VisitorAnalytics />
            </TabsContent>

            {/* Maintenance Tab */}
            <TabsContent value="maintenance">
              <MaintenanceMode />
            </TabsContent>

            {/* Branding Tab */}
            <TabsContent value="branding" className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold mb-4">Website Logo</h2>
                <div className="bg-card rounded-2xl p-6 border border-border/50 space-y-4">
                  {currentLogo ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center p-8 bg-secondary/30 rounded-xl">
                        <img
                          src={`${BACKEND_URL}${currentLogo}`}
                          alt="Site Logo"
                          className="max-h-32 object-contain"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Label
                          htmlFor="logo-upload-replace"
                          className="flex-1 flex items-center justify-center gap-2 p-4 border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all cursor-pointer"
                        >
                          <Upload className="w-5 h-5" />
                          <span>Neues Logo hochladen</span>
                        </Label>
                        <Button
                          variant="destructive"
                          onClick={handleDeleteLogo}
                          className="gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Logo löschen
                        </Button>
                      </div>
                      <input
                        id="logo-upload-replace"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleUploadLogo(e.target.files[0]);
                          }
                        }}
                        disabled={uploadingLogo}
                      />
                    </div>
                  ) : (
                    <div>
                      <Label
                        htmlFor="logo-upload"
                        className="flex items-center justify-center gap-2 p-8 border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all cursor-pointer"
                      >
                        <ImageIcon className="w-8 h-8" />
                        <div className="text-center">
                          <p className="font-medium">Logo hochladen</p>
                          <p className="text-sm text-muted-foreground">PNG, JPG oder SVG (max. 2MB)</p>
                        </div>
                      </Label>
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleUploadLogo(e.target.files[0]);
                          }
                        }}
                        disabled={uploadingLogo}
                      />
                    </div>
                  )}

                  {uploadingLogo && (
                    <div className="flex items-center justify-center gap-2 py-4">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-muted-foreground">Wird hochgeladen...</span>
                    </div>
                  )}

                  <div className="bg-secondary/30 rounded-xl p-4">
                    <p className="text-sm text-muted-foreground">
                      💡 <strong>Hinweis:</strong> Das Logo wird automatisch auf allen Seiten der Website angezeigt
                      (Header, Footer, Landing Page). Nach dem Upload wird die Seite neu geladen, um das Logo überall zu aktualisieren.
                    </p>
                  </div>
                </div>

                <h2 className="text-xl font-semibold mb-4 mt-8">Website Favicon</h2>
                <div className="bg-card rounded-2xl p-6 border border-border/50 space-y-4">
                  {currentFavicon ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center p-8 bg-secondary/30 rounded-xl">
                        <img
                          src={`${BACKEND_URL}${currentFavicon}`}
                          alt="Site Favicon"
                          className="w-16 h-16 object-contain shadow-sm"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Label
                          htmlFor="favicon-upload-replace"
                          className="flex-1 flex items-center justify-center gap-2 p-4 border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all cursor-pointer"
                        >
                          <Upload className="w-5 h-5" />
                          <span>Neues Favicon hochladen</span>
                        </Label>
                        <Button
                          variant="destructive"
                          onClick={handleDeleteFavicon}
                          className="gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Löschen
                        </Button>
                      </div>
                      <input
                        id="favicon-upload-replace"
                        type="file"
                        accept="image/png,image/x-icon"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleUploadFavicon(e.target.files[0]);
                          }
                        }}
                        disabled={uploadingFavicon}
                      />
                    </div>
                  ) : (
                    <div>
                      <Label
                        htmlFor="favicon-upload"
                        className="flex items-center justify-center gap-2 p-8 border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all cursor-pointer"
                      >
                        <ImageIcon className="w-8 h-8" />
                        <div className="text-center">
                          <p className="font-medium">Favicon hochladen</p>
                          <p className="text-sm text-muted-foreground">PNG oder ICO (max. 500KB)</p>
                        </div>
                      </Label>
                      <input
                        id="favicon-upload"
                        type="file"
                        accept="image/png,image/x-icon"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleUploadFavicon(e.target.files[0]);
                          }
                        }}
                        disabled={uploadingFavicon}
                      />
                    </div>
                  )}

                  {uploadingFavicon && (
                    <div className="flex items-center justify-center gap-2 py-4">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-muted-foreground">Wird hochgeladen...</span>
                    </div>
                  )}

                  <div className="bg-secondary/30 rounded-xl p-4">
                    <p className="text-sm text-muted-foreground">
                      💡 <strong>Hinweis:</strong> Das Favicon ist das kleine Symbol oben im Browser-Tab. Nach dem Upload wird die Seite neu geladen.
                    </p>
                  </div>
                </div>
              </div>

              {/* Branding Colors Section */}
              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4">Branding Farben</h2>
                <div className="bg-card rounded-2xl p-6 border border-border/50 space-y-6">

                  {/* Color Inputs Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Primary Color */}
                    <div className="space-y-2">
                      <Label htmlFor="primary-color" className="font-medium">Primärfarbe</Label>
                      <p className="text-xs text-muted-foreground">Hauptfarbe für Buttons, Links und Akzente</p>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          id="primary-color"
                          value={brandingColors.primary_color}
                          onChange={(e) => setBrandingColors({ ...brandingColors, primary_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                        />
                        <Input
                          value={brandingColors.primary_color}
                          onChange={(e) => setBrandingColors({ ...brandingColors, primary_color: e.target.value })}
                          placeholder="#2a6a68"
                          className="flex-1 font-mono"
                        />
                      </div>
                    </div>

                    {/* Accent Color */}
                    <div className="space-y-2">
                      <Label htmlFor="accent-color" className="font-medium">Akzentfarbe</Label>
                      <p className="text-xs text-muted-foreground">Sekundäre Farbe für Highlights und Erfolge</p>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          id="accent-color"
                          value={brandingColors.accent_color}
                          onChange={(e) => setBrandingColors({ ...brandingColors, accent_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                        />
                        <Input
                          value={brandingColors.accent_color}
                          onChange={(e) => setBrandingColors({ ...brandingColors, accent_color: e.target.value })}
                          placeholder="#10b981"
                          className="flex-1 font-mono"
                        />
                      </div>
                    </div>

                    {/* Text Color */}
                    <div className="space-y-2">
                      <Label htmlFor="text-color" className="font-medium">Textfarbe</Label>
                      <p className="text-xs text-muted-foreground">Farbe für Überschriften und wichtigen Text</p>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          id="text-color"
                          value={brandingColors.text_color}
                          onChange={(e) => setBrandingColors({ ...brandingColors, text_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                        />
                        <Input
                          value={brandingColors.text_color}
                          onChange={(e) => setBrandingColors({ ...brandingColors, text_color: e.target.value })}
                          placeholder="#1f2937"
                          className="flex-1 font-mono"
                        />
                      </div>
                    </div>

                    {/* Background Color */}
                    <div className="space-y-2">
                      <Label htmlFor="background-color" className="font-medium">Hintergrundfarbe</Label>
                      <p className="text-xs text-muted-foreground">Haupt-Hintergrundfarbe der App</p>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          id="background-color"
                          value={brandingColors.background_color}
                          onChange={(e) => setBrandingColors({ ...brandingColors, background_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                        />
                        <Input
                          value={brandingColors.background_color}
                          onChange={(e) => setBrandingColors({ ...brandingColors, background_color: e.target.value })}
                          placeholder="#ffffff"
                          className="flex-1 font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preview Section */}
                  <div className="border-t border-border pt-6">
                    <h3 className="font-medium mb-4">Vorschau</h3>
                    <div
                      className="rounded-xl p-6 border"
                      style={{ backgroundColor: brandingColors.background_color }}
                    >
                      <h4 style={{ color: brandingColors.text_color }} className="text-lg font-semibold mb-2">
                        Beispiel Überschrift
                      </h4>
                      <p style={{ color: brandingColors.text_color }} className="text-sm opacity-70 mb-4">
                        Dies ist ein Beispieltext, um die Farben zu demonstrieren.
                      </p>
                      <div className="flex gap-3">
                        <button
                          style={{ backgroundColor: brandingColors.primary_color }}
                          className="px-4 py-2 rounded-lg text-white font-medium"
                        >
                          Primär Button
                        </button>
                        <button
                          style={{ backgroundColor: brandingColors.accent_color }}
                          className="px-4 py-2 rounded-lg text-white font-medium"
                        >
                          Akzent Button
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-4 border-t border-border">
                    <Button
                      onClick={handleSaveColors}
                      disabled={savingColors}
                      className="gap-2"
                    >
                      {savingColors ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Speichern...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Farben speichern
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="bg-secondary/30 rounded-xl p-4">
                    <p className="text-sm text-muted-foreground">
                      💡 <strong>Hinweis:</strong> Die Farben werden nach dem Speichern auf der gesamten Website angewendet.
                      Die Seite wird automatisch neu geladen, um die Änderungen zu übernehmen.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* System Settings Tab */}
            <TabsContent value="settings">
              <SystemSettings />
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
